/**
 * <ProvenByTests /> — trust badge for engine pages, NOW LIVE.
 *
 * Fetches the test count from the public /api/algorithm-correctness/manifest
 * endpoint on mount, so the badge auto-updates as we add new tests. Falls
 * back to the static count if the API is unreachable (no broken badge ever).
 *
 * Public verification: anyone can `curl https://cartieai.com/api/algorithm-correctness/manifest`
 * to verify the claim — the endpoint parses our actual test source file via
 * Python's `ast` module, not a hard-coded marketing number.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, ChevronRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static fallback — used when the API is unreachable or returns garbage.
// Should match what's in the test file at last commit time.
const STATIC_FALLBACK = {
  shapley: {
    label: 'K8s Shapley',
    test_count: 7,
    axioms: [
      'Symmetry — identical pods get equal shares',
      'Efficiency — Σ φᵢ ≡ v(N) within 2% rel err',
      'Proportionality — bigger pods get more',
      'Dummy axiom — singletons bear full overhead',
      'Monte-Carlo convergence to brute-force exact (4000 samples, ≤5% per-pod err)',
    ],
  },
  bisect: {
    label: 'Cost-Bisect',
    test_count: 6,
    axioms: [
      'Finds the EARLIEST regression, not the largest',
      'Threshold-exact boundary handled (>= semantics)',
      'Range-swap auto-corrects (from > to)',
      'Unknown SHA raises 404 cleanly',
      'No-regression case returns found=false',
    ],
  },
  'causal-bot': {
    label: 'Causal Bot',
    test_count: 15,
    axioms: [
      '12 parametrized intent-routing cases across all 5 intents',
      'Unknown questions default to cost-spike',
      'Causal chain has ≥2 sub-systems',
      'All dollar impacts ≥ 0, finite, sane range',
    ],
  },
};

// Module-level singleton so the badge doesn't fetch N times on a page with N badges.
let _manifestCache = null;
let _manifestPromise = null;
const fetchManifest = () => {
  if (_manifestCache) return Promise.resolve(_manifestCache);
  if (_manifestPromise) return _manifestPromise;
  _manifestPromise = fetch(`${API_URL}/algorithm-correctness/manifest`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((data) => { _manifestCache = data; return data; })
    .catch(() => null)
    .finally(() => { _manifestPromise = null; });
  return _manifestPromise;
};

export const ProvenByTests = ({ engine, className = '' }) => {
  const fallback = STATIC_FALLBACK[engine];
  const [liveCount, setLiveCount] = useState(null);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchManifest().then((data) => {
      if (!mounted || !data?.engines) return;
      const row = data.engines.find((e) => e.engine === engine);
      if (row && Number.isFinite(row.test_count)) {
        setLiveCount(row.test_count);
        setIsLive(true);
      }
    });
    return () => { mounted = false; };
  }, [engine]);

  if (!fallback) return null;
  const displayCount = liveCount ?? fallback.test_count;
  const tooltip = `${fallback.axioms.join(' · ')}${isLive ? ' · live manifest at /api/algorithm-correctness/manifest' : ''}`;

  return (
    <a
      href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/algorithm-correctness/manifest`}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-[11px] font-mono hover:bg-emerald-500/20 hover:border-emerald-400/60 transition-all group cursor-pointer ${className}`}
      data-testid={`proven-by-tests-${engine}`}
      data-live={isLive}
      title={tooltip}
    >
      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 group-hover:rotate-6 transition-transform" />
      <span className="font-semibold">
        Proven by <span className="text-emerald-300">{displayCount}</span> mathematical tests
      </span>
      {isLive && (
        <span className="text-[9px] text-emerald-400/80 uppercase tracking-widest font-bold animate-pulse">LIVE</span>
      )}
      <ChevronRight className="w-3 h-3 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
    </a>
  );
};

export default ProvenByTests;
